import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  Select,
  Result,
  MathDiv,
  ActionButtonsRow,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  CopyHTMLButton,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
import { roundToDecimals } from '../../../utils/numberFormat'

const MODES = [
  { value: 'resistance', label: 'Obter resistência' },
  { value: 'thickness', label: 'Obter espessura' },
  { value: 'diameter', label: 'Obter diâmetro' },
]

export function SteelCircularTubeBending() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [mode, setMode] = useState('resistance')

  // resistance inputs
  const [d, setD] = useState('')
  const [t, setT] = useState('')

  // thickness/diameter inputs
  const [msk, setMsk] = useState('')
  const [gammaf, setGammaf] = useState(1.5)

  // common
  const [fy, setFy] = useState(240)
  const [e, setE] = useState(200000)

  const [result, setResult] = useState(null)
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'steel-circular-tube-bending-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const saved = localStorage.getItem(HISTORY_KEY)
    if (saved) {
      try {
        setHistory(JSON.parse(saved))
      } catch {
        // ignore
      }
    }
  }, [])

  const saveToHistory = useCallback((entry) => {
    setHistory((prev) => {
      const updated = [{ ...entry, timestamp: new Date().toISOString(), id: Date.now() }, ...prev].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(updated))
      return updated
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return
      const entry = history[index]
      setMode(entry.mode)
      setD(entry.d ?? '')
      setT(entry.t ?? '')
      setMsk(entry.msk ?? '')
      setGammaf(entry.gammaf ?? 1.4)
      setFy(entry.fy)
      setE(entry.e ?? '')
      setData(entry.data ?? {})
      setResult(entry.result)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) loadFromHistory(newIndex)
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) return 'Campo obrigatório'
    const num = Number(value)
    if (isNaN(num)) return 'Valor deve ser um número'
    if (num <= 0) return 'Valor deve ser maior que 0'
    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fy = validateField('fy', fy)
    newErrors.e = validateField('e', e)

    if (mode === 'resistance') {
      newErrors.d = validateField('d', d)
      newErrors.t = validateField('t', t)
      // geometry: t < D/2
      if (!newErrors.d && !newErrors.t) {
        const dNum = Number(d)
        const tNum = Number(t)
        if (tNum >= dNum / 2) {
          newErrors.t = 't deve ser menor que D/2'
        }
      }
    } else if (mode === 'thickness') {
      newErrors.msk = validateField('msk', msk)
      newErrors.gammaf = validateField('gammaf', gammaf)
      newErrors.d = validateField('d', d)
    } else if (mode === 'diameter') {
      newErrors.msk = validateField('msk', msk)
      newErrors.gammaf = validateField('gammaf', gammaf)
      newErrors.t = validateField('t', t)
    }

    setErrors(newErrors)
    setIsFormValid(!Object.values(newErrors).some(Boolean))
  }, [mode, d, t, msk, gammaf, fy, e, validateField])

  const handleBlur = (fieldName) => setTouched((prev) => ({ ...prev, [fieldName]: true }))

  function buildPayload() {
    const payload = { mode, fy: Number(fy), e: Number(e) }

    if (mode === 'resistance') {
      payload.d = Number(d)
      payload.t = Number(t)
    } else if (mode === 'thickness') {
      payload.msk = Number(msk)
      payload.gammaf = Number(gammaf)
      payload.d = Number(d)
    } else if (mode === 'diameter') {
      payload.msk = Number(msk)
      payload.gammaf = Number(gammaf)
      payload.t = Number(t)
    }

    return payload
  }

  async function submitPayload(payload) {
    const response = await api.post('/steel/circular-tube-bending', payload)

    if (response.rateLimit) {
      updateFromHeaders({
        'x-ratelimit-limit': response.rateLimit.limit.toString(),
        'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
        'x-ratelimit-reset': response.rateLimit.reset,
      })
    }

    await fetchRateLimitStatus()
    return response.data
  }

  async function handleCalculate(event) {
    event.preventDefault()
    const hasExistingResults = result !== null
    setIsLoading(true)
    if (hasExistingResults) setIsRecalculating(true)

    try {
      const payload = buildPayload()
      setData(payload)
      const responseData = await submitPayload(payload)
      setResult({ ...responseData, _mode: mode })

      saveToHistory({ mode, d, t, msk, gammaf, fy, e, data: payload, result: { ...responseData, _mode: mode } })
      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)
      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }
      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) setTimeout(() => setIsRecalculating(false), 500)
    }
  }

  function handleCopyResults() {
    copyResultsToClipboard({ result })
  }

  function handleSave() {
    const stateData = { mode, d, t, msk, gammaf, fy, e, data, result }
    const blob = new Blob([JSON.stringify(stateData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Aço Tubo Circular Flexão.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target.result)
        setMode(json.mode ?? 'resistance')
        setD(json.d ?? '')
        setT(json.t ?? '')
        setMsk(json.msk ?? '')
        setGammaf(json.gammaf ?? 1.4)
        setFy(json.fy ?? 250)
        setE(json.e ?? '')
        setData(json.data ?? {})
        setResult(json.result ?? null)
        toast.success('Estado carregado com sucesso!')
      } catch {
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleModeChange(event) {
    setMode(event.target.value)
    setResult(null)
    setTouched({})
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #2c3e50 0%, #34495e 100%)">
        <div className="header-content">
          <BackButton to="/aco">
            <FiArrowLeft /> Voltar para Aço
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/aco">Aço</a>
            <span>/</span>
            <span>Tubo circular à flexão</span>
          </div>
          <h1>⭕ Tubo circular à flexão</h1>
          <p className="subtitle">Dimensionamento e verificação de seções tubulares circulares sob momento fletor</p>
          <div className="badges">
            <Badge>📘 ABNT NBR 8800:2008</Badge>
            <Badge>⭕ Seções tubulares</Badge>
            <Badge>📐 Flexão simples</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #e8f4f8 0%, #cfe9f3 100%)"
        $border="2px solid #a8d8ea"
        $primaryColor="#2c3e50"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta calculadora</h3>
            <p>
              Calcula o momento resistente de tubos circulares (modo resistência) ou realiza pré-dimensionamento da
              espessura ou do diâmetro a partir do momento solicitante. A classificação da seção (compacta,
              semicompacta, esbelta) é feita automaticamente no modo resistência.
            </p>
            <p>
              Os modos espessura e diâmetro usam a expressão da seção compacta e fornecem dimensionamento preliminar.
              Use o modo resistência para validação final.
            </p>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />
          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculate}>
            <h3>Modo de cálculo</h3>
            <InputDiv>
              <span>Modo</span>
              <div>
                <Select style={{ marginLeft: '0' }} value={mode} onChange={handleModeChange}>
                  {MODES.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>

            <h3>Material</h3>
            <InputDiv>
              <span>Resistência ao escoamento fy (MPa)</span>
              <ValidatedInput
                name="fy"
                type="number"
                value={fy}
                onChange={(ev) => setFy(ev.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Módulo de elasticidade E (MPa)</span>
              <ValidatedInput
                name="e"
                type="number"
                value={e}
                onChange={(ev) => setE(ev.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <h3>Geometria</h3>
            {(mode === 'resistance' || mode === 'thickness') && (
              <InputDiv>
                <span>Diâmetro externo D (mm)</span>
                <ValidatedInput
                  name="d"
                  type="number"
                  value={d}
                  onChange={(ev) => setD(ev.target.value)}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            {(mode === 'resistance' || mode === 'diameter') && (
              <InputDiv>
                <span>Espessura da parede t (mm)</span>
                <ValidatedInput
                  name="t"
                  type="number"
                  value={t}
                  onChange={(ev) => setT(ev.target.value)}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}

            {(mode === 'thickness' || mode === 'diameter') && (
              <>
                <h3>Esforços</h3>
                <InputDiv>
                  <span>Momento característico Msk (kN.cm)</span>
                  <ValidatedInput
                    name="msk"
                    type="number"
                    value={msk}
                    onChange={(ev) => setMsk(ev.target.value)}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Coeficiente de ponderação γf</span>
                  <ValidatedInput
                    name="gammaf"
                    type="number"
                    value={gammaf}
                    onChange={(ev) => setGammaf(ev.target.value)}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
              </>
            )}

            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('circular-tube-file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input
              type="file"
              id="circular-tube-file-input"
              accept=".json"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </form>
        </Card>

        {result !== null && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>

              {result._mode === 'resistance' && (
                <MathDiv>
                  <h3>Classificação da seção</h3>
                  {result.classification.case === 1 && (
                    <>
                      <BlockMath
                        math={`\\lambda =  ${roundToDecimals(result.lambda)} \\leq \\lambda_p \\Rightarrow \\text{Seção compacta}`}
                      />
                      <h3>Momento resistente</h3>
                      <BlockMath math={`M_{Rd} = ${roundToDecimals(result.mrd)} \\, \\text{kN.cm}`} />
                    </>
                  )}
                  {result.classification.case === 2 && (
                    <>
                      <BlockMath
                        math={`\\lambda_p < \\lambda =  ${roundToDecimals(result.lambda)} \\leq \\lambda_r \\Rightarrow \\text{Seção semi-compacta}`}
                      />
                      <h3>Momento resistente</h3>
                      <BlockMath math={`M_{Rd} = ${roundToDecimals(result.mrd)} \\, \\text{kN.cm}`} />
                    </>
                  )}
                  {result.classification.case === 3 && (
                    <>
                      <BlockMath
                        math={`\\lambda =  ${roundToDecimals(result.lambda)} > \\lambda_r \\Rightarrow \\text{Seção esbelta}`}
                      />
                      <h3>Momento resistente</h3>
                      <BlockMath math={`M_{Rd} = ${roundToDecimals(result.mrd)} \\, \\text{kN.cm}`} />
                    </>
                  )}
                </MathDiv>
              )}

              {result._mode === 'thickness' && (
                <MathDiv>
                  <h3>Espessura necessária</h3>
                  <BlockMath
                    math={`t = ${roundToDecimals(result.finalT_cm * 10)} \\text{ mm} \\Rightarrow ${result.commercialT_mm} \\text{ mm}`}
                  />
                </MathDiv>
              )}

              {result._mode === 'diameter' && (
                <MathDiv>
                  <h3>Diâmetro necessário</h3>
                  {result.passes ? (
                    <BlockMath
                      math={`D = ${roundToDecimals(result.d_cm * 10)} \\text{ mm} \\Rightarrow \\boxed{ ${result.commercialD_mm} \\text{ mm} }`}
                    />
                  ) : (
                    <p style={{ color: 'red' }}>Impossível de resolver com seção compacta.</p>
                  )}
                </MathDiv>
              )}
            </Result>

            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>

              <MathDiv>
                {result._mode === 'resistance' &&
                  (() => {
                    return (
                      <>
                        <h3>Classificação da seção</h3>
                        <BlockMath math={`\\gamma_{a1} = 1.1`} />
                        <BlockMath
                          math={`\\lambda_p = 0.07 \\cdot \\frac{E}{f_y} = 0.07 \\cdot \\frac{${data.e}}{${data.fy}} = ${roundToDecimals(result.limits.lambdaP)}`}
                        />
                        <BlockMath
                          math={`\\lambda_r = 0.31 \\cdot \\frac{E}{f_y} = 0.31 \\cdot \\frac{${data.e}}{${data.fy}} = ${roundToDecimals(result.limits.lambdaR)}`}
                        />
                        <BlockMath
                          math={`\\lambda = \\frac{D}{t} = \\frac{${data.d}}{${data.t}} = ${roundToDecimals(result.lambda)}`}
                        />
                        {result.classification.case === 1 && (
                          <>
                            <BlockMath
                              math={`\\lambda =  ${roundToDecimals(result.lambda)} \\leq \\lambda_p \\Rightarrow \\text{Seção compacta}`}
                            />
                            <h3>Momento resistente</h3>
                            <BlockMath
                              math={`M_{Rd} = \\dfrac{M_{pl}}{\\gamma_{a1}} = \\dfrac{Z_x \\cdot F_y}{\\gamma_{a1}}`}
                            />
                            <BlockMath
                              math={`M_{Rd} = \\dfrac{4 \\cdot R^2 \\cdot t \\cdot F_y}{\\gamma_{a1}} = \\dfrac{4 \\cdot ${data.d / 200}^2 \\cdot ${data.t / 10} \\cdot ${data.fy / 10}}{1.1}`}
                            />
                            <BlockMath math={`\\boxed{M_{Rd} = ${roundToDecimals(result.mrd)} \\, \\text{kN.cm}}`} />
                          </>
                        )}
                        {result.classification.case === 2 && (
                          <>
                            <BlockMath
                              math={`\\lambda_p < \\lambda =  ${roundToDecimals(result.lambda)} \\leq \\lambda_r \\Rightarrow \\text{Seção semi-compacta}`}
                            />
                            <h3>Momento resistente</h3>
                            <BlockMath
                              math={`M_{Rd} = \\dfrac{1}{\\gamma_{a1}} \\cdot \\left ( \\dfrac{0.021 \\cdot E}{\\lambda} + F_y \\right) \\cdot W`}
                            />
                            <BlockMath
                              math={`M_{Rd} = \\dfrac{1}{1.1} \\cdot \\left ( \\dfrac{0.021 \\cdot ${data.e / 10} }{ ${roundToDecimals(result.lambda)} } + ${data.fy / 10} \\right) \\cdot ${roundToDecimals(result.w, 3)} `}
                            />
                            <BlockMath math={`\\boxed{M_{Rd} = ${roundToDecimals(result.mrd)} \\, \\text{kN.cm}}`} />
                          </>
                        )}
                        {result.classification.case === 3 && (
                          <>
                            <BlockMath
                              math={`\\lambda =  ${roundToDecimals(result.lambda)} > \\lambda_r \\Rightarrow \\text{Seção esbelta}`}
                            />
                            <h3>Momento resistente</h3>
                            <BlockMath
                              math={`M_{Rd} = \\dfrac{1}{\\gamma_{a1}} \\cdot \\left ( \\dfrac{0.33 \\cdot E}{\\lambda} \\right) \\cdot W`}
                            />
                            <BlockMath
                              math={`M_{Rd} = \\dfrac{1}{1.1} \\cdot \\left ( \\dfrac{0.33 \\cdot ${data.e / 10} }{ ${roundToDecimals(result.lambda)} } \\right) \\cdot ${roundToDecimals(result.w, 3)} `}
                            />
                            <BlockMath math={`\\boxed{M_{Rd} = ${roundToDecimals(result.mrd)} \\, \\text{kN.cm}}`} />
                          </>
                        )}
                      </>
                    )
                  })()}

                {result._mode === 'thickness' &&
                  (() => {
                    return (
                      <>
                        <h3>Espessura necessária</h3>
                        <BlockMath
                          math={`\\lambda_p = 0.07 \\cdot \\frac{E}{f_y} = 0.07 \\cdot \\frac{${data.e}}{${data.fy}} = ${roundToDecimals(result.lambdaP)}`}
                        />
                        <BlockMath
                          math={`\\lambda = \\dfrac{D}{t} = \\dfrac{ ${data.d} }{t} \\leq ${roundToDecimals(result.lambdaP)}`}
                        />
                        <BlockMath
                          math={`t \\geq ${roundToDecimals(result.lowerT_cm * 10)} \\text{ mm} \\Rightarrow \\text{ garante a seção compacta}`}
                        />
                        <p>Valor mínimo para resistir:</p>
                        <BlockMath math={`t \\geq \\dfrac{M_{Sd} \\cdot 1.1}{4 \\cdot R^2 \\cdot F_y}`} />
                        <BlockMath
                          math={`t \\geq \\dfrac{ ${roundToDecimals(result.msd_kncm)}  \\cdot 1.1}{4 \\cdot ${data.d / 20} ^2 \\cdot ${data.fy / 10}} = ${roundToDecimals(result.t_cm, 4)} \\rightarrow ${roundToDecimals(result.t_cm * 10, 2)} \\text{ mm}`}
                        />
                        <BlockMath
                          math={`t = ${roundToDecimals(result.finalT_cm * 10)} \\text{ mm} \\Rightarrow \\boxed{${result.commercialT_mm} \\text{ mm}}`}
                        />
                      </>
                    )
                  })()}

                {result._mode === 'diameter' &&
                  (() => {
                    return (
                      <>
                        <h3>Diâmetro necessário</h3>
                        <BlockMath
                          math={`\\lambda_p = 0.07 \\cdot \\frac{E}{f_y} = 0.07 \\cdot \\frac{${data.e}}{${data.fy}} = ${roundToDecimals(result.lambdaP)}`}
                        />
                        <BlockMath
                          math={`\\lambda = \\dfrac{D}{t} = \\dfrac{ D }{ ${data.t} } \\leq ${roundToDecimals(result.lambdaP)}`}
                        />
                        <BlockMath
                          math={`D \\leq ${roundToDecimals(result.higherD_cm * 10)} \\text{ mm} \\Rightarrow \\text{ garante a seção compacta}`}
                        />
                        <p>Valor mínimo para resistir:</p>
                        <BlockMath math={`R \\geq \\dfrac{M_{Sd} \\cdot 1.1}{4 \\cdot t \\cdot F_y}`} />
                        <BlockMath
                          math={`R \\geq \\dfrac{ ${roundToDecimals(result.msd_kncm)} \\cdot 1.1}{4 \\cdot ${data.t / 10} \\cdot ${data.fy / 10}} = ${roundToDecimals(result.r_cm)} \\text{ cm}`}
                        />
                        <BlockMath math={`D \\geq ${roundToDecimals(result.d_cm * 10)} \\text{ mm}`} />
                        {result.passes ? (
                          <BlockMath
                            math={`D = ${roundToDecimals(result.d_cm * 10)} \\text{ mm} \\Rightarrow \\boxed{ ${result.commercialD_mm} \\text{ mm} }`}
                          />
                        ) : (
                          <p style={{ color: 'red' }}>Impossível de resolver com seção compacta.</p>
                        )}
                      </>
                    )
                  })()}
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
